<template>
  <div>
    <div class="title-bar title-h4 bold" style="height:32px">{{title}}</div>
    <div v-loading="isLoading" style="overflow:auto">
      <highcharts v-if="!isEmptyData" :options="options" ref="highchart" style="height:180px;min-width:330px"/>
      <div v-else class="empty_box">
        <span class="empty_text">데이터가 없습니다.</span>
      </div>
    </div>
  </div>
</template>

<script>
import { isNil, isEmpty, cloneDeep, isEqual, map } from 'lodash';
import { pieOptions } from '@/constants/chart-options';
import EventHistAPI from '@/api/event/event-hist';
import EventHistStatMixins from '@/mixins/event/event-hist-stats';

export default {
  name: 'WEventHistPieChart',
  mixins: [EventHistStatMixins],
  computed: {
    resultStatusFilters() {
      const resultStatusFilters = this.filterList.filter((item) => isEqual(item.type, 'resultStatus'));
      const result = map(resultStatusFilters, 'label');
      return result;
    },
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    chartType: {
      type: String,
      default: null,
    },
    filterList: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: null,
    },
    currentFilterCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isLoading: false,
      totalCount: 0,
      options: cloneDeep(pieOptions),
      isEmptyData: true,
      customLegendOptions: {},
      isBlack: true,
    };
  },
  created() {
    this.setChartOption();
  },
  watch: {
    active: {
      handler(value) {
        if (!isNil(value) && value) {
          this.loadData();
        }
      },
      immediate: true,
    },
    selectedNode() {
      this.loadData();
    },
    analysisDateParams() {
      this.loadData();
    },
  },
  methods: {
    loadData() {
      if (!this.active) {
        return;
      }
      this.isLoading = true;
      const params = {
        treeType: this.selectedTreeType,
        type: this.selectedNode.type,
        startDatetime: this.analysisDateParams.startDatetime,
        endDatetime: this.analysisDateParams.endDatetime,
      };
      this.setEventHistStatsParams(params);
      if (isEqual(this.chartType, 'eventlevel')) {
        EventHistAPI.getEventHistStatsGroupByEventlevel(params).then((response) => {
          const result = response.data;
          if (result.success) {
            this.setChartData(cloneDeep(result.data));
          }
        }).finally(() => {
          this.isLoading = false;
        });
      } else {
        EventHistAPI.getEventHistStatsGroupByObjDefine(params).then((response) => {
          const result = response.data;
          if (result.success) {
            this.setChartData(cloneDeep(result.data));
          }
        }).finally(() => {
          this.isLoading = false;
        });
      }
    },
    setChartData(data) {
      const totalData = data.filter((item) => item.eventlevelId === '_TOTAL' || item.objDefineId === '_TOTAL')[0];
      const realData = data.filter((item) => item.eventlevelId !== '_TOTAL' && item.objDefineId !== '_TOTAL');
      this.isEmptyData = isEmpty(realData);
      this.totalCount = totalData.eventCount;
      if (this.chartType === 'eventlevel') {
        realData.forEach((item) => {
          item.y = isNil(item.eventCount) ? null : Number(item.eventCount);
          item.name = isNil(item.eventlevelId) ? '' : this.eventlevelInfo[item.eventlevelId].eventlevelName;
          item.color = isNil(item.eventlevelId) ? '' : this.eventlevelInfo[item.eventlevelId].eventlevelColorBg;
        });
      } else {
        realData.forEach((item) => {
          item.y = isNil(item.eventCount) ? null : Number(item.eventCount);
          item.name = isNil(item.objDefineName) ? '' : item.objDefineName;
        });
      }
      this.options.series = [
        {
          name: '건수',
          colorByPoint: true,
          data: realData,
        },
      ];
      // this.$nextTick(() => {
      //   if (!isEmpty(this.resultStatusFilters)) {
      //     const chartData = this.$refs.highchart.chart.series[0].data;
      //     this.resultStatusFilters.forEach((filter) => {
      //       const item = chartData.filter((o) => o.name === filter);
      //       if (!isEmpty(item)) {
      //         item[0].select(null, true);
      //       }
      //     });
      //   }
      // });
    },
    setChartOption() {
      const me = this;
      this.options.chart.spacing = [0, 0, 0, 0];
      this.options.chart.events = {
        load() {
          const chart = this;
          chart.setTitle({
            text: `${me.convertValueByUnitData(me.totalCount, 'count')}<br/> <span style="font-size:11px">Total</span>`,
            align: 'center',
            verticalAlign: 'middle',
            x: -100,
            y: 0,
            style: {
              fontWeight: 'bold',
            },
          });
        },
      };
      this.options.plotOptions = {
        series: {
          className: 'c-pointer',
          point: {
            events: {
              click() {
                if (!this.selected && me.currentFilterCount === 10) {
                  me.$message('필터는 10개까지 가능합니다');
                  return;
                }
                this.slice(null);
                this.select(null, true);
                const item = {
                  ...this.options,
                  tagType: me.chartType === 'eventlevel' ? 'danger' : 'success',
                  type: me.chartType,
                };
                me.$emit('set-event-hist-filter', item);
              },
            },
          },
        },
        pie: {
          dataLabels: {
            enabled: false,
          },
          showInLegend: true,
          innerSize: '70%',
        },
      };
      this.options.legend = {
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
        width: 180,
        labelFormatter() {
          return `<label title=${this.name} style="cursor:pointer">${this.name} (${me.convertValueByUnitData(this.y, 'count')})</label>`;
        },
        useHTML: true,
      };
    },
    deleteEventHistFilter(filter) {
      this.$nextTick(() => {
        if (this.$refs.highchart) {
          const data = this.$refs.highchart.chart.series[0].data;
          if (!isNil(filter)) {
            const item = data.filter((o) => o.name === filter.name)[0];
            if (!isNil(item)) {
              item.select(null, true);
            }
          } else {
            data[0].select(false);
          }
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.empty_box {
  width: 100%;
  min-height: 180px;
  display: table;
  .empty_text {
    display: table-cell;
    vertical-align: middle;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/event/event-hist/components/EventHistPieChart.vue